var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/partner-api-client.ts
import { HttpClient } from "@repo/lib-http-client";
import { FlightStatusProviderEnum } from "@repo/lib-koala-types";
import { confirmBookingCancellationResBodySchema, createAccountTokenResBodySchema, createBundleQuoteResBodySchema, createClaimGoodwillGestureResBodySchema, createClaimRefundResBodySchema, createClaimRejectionResBodySchema, createClaimResBodySchema, createClaimVerificationResBodySchema, createFlightMonitoringResBodySchema, createFlightTrackerResBodySchema, createQuoteResBodySchema, createSubscribedCustomerTokenResBodySchema, createSubscriptionPaymentsResBodySchema, createSubscriptionResBodySchema, deleteQuoteResBodySchema, deleteStaleContractDocumentsResBodySchema, findOneContractResBodySchema, getAllSubscriptionContractsResBodySchema, getDashboardReferenceResBodySchema, getDashboardSnapshotResBodySchema, getDocumentResBodySchema, getPartnerOverviewResBodySchema, listPartnersResBodySchema, listSubscriptionsResBodySchema, reopenPaymentWindowResBodySchema, replaceDocumentResBodySchema, updateDocumentResBodySchema, updateSubscriptionResBodySchema, upsertExternalClaimResBodySchema } from "@repo/schema-api-partner";
var PartnerApiClient = class {
  static {
    __name(this, "PartnerApiClient");
  }
  httpClient;
  constructor({ baseUrl, koalaToken, timeout, headers }) {
    this.httpClient = new HttpClient({
      baseURL: baseUrl,
      headers: {
        Authorization: `Bearer ${koalaToken}`,
        "Content-Type": "application/json",
        ...headers ?? {}
      },
      timeout: timeout ?? 2e3
    });
  }
  async createAccountToken(data) {
    return this.httpClient.post("/tokens", {
      data,
      expectedStatus: 201,
      schema: createAccountTokenResBodySchema
    });
  }
  async createSubscribedCustomerToken(data) {
    return this.httpClient.post("/tokens/subscribed-customer", {
      data,
      expectedStatus: 201,
      schema: createSubscribedCustomerTokenResBodySchema
    });
  }
  async addFlightStatus({ airlineIata, departureAirportIata, number, localDepartureDate }, data) {
    return this.httpClient.post(`/flights/${airlineIata}/${number}/${localDepartureDate}/${departureAirportIata}/statuses`, {
      data: {
        data
      },
      expectedStatus: 201
    });
  }
  async requestFlightStatus({ airlineIata, number, localDepartureDate, departureAirportIata }) {
    const data = {
      provider: FlightStatusProviderEnum.FLIGHT_STATS
    };
    return this.httpClient.post(`/flights/${airlineIata}/${number}/${localDepartureDate.toUTC().toISODate()}/${departureAirportIata}/tracker-requests`, {
      data: {
        data
      },
      expectedStatus: 201,
      schema: createFlightTrackerResBodySchema
    });
  }
  async newClaim(contractId, { data, silent = false, sideEffects = true, dryRun = false, bypassAdditionalChecks = false } = {}) {
    return this.httpClient.post(`/contracts/${contractId}/claims`, {
      data: data !== void 0 ? {
        data
      } : {},
      expectedStatus: 201,
      params: {
        silent,
        sideEffects,
        // Only sent when requested: the query schema is strict, so an API not yet exposing `dryRun` rejects it.
        ...dryRun ? {
          dryRun
        } : {},
        ...bypassAdditionalChecks ? {
          bypassAdditionalChecks
        } : {}
      },
      schema: createClaimResBodySchema
    });
  }
  async verifyClaim(contractId, { silent = false, sideEffects = true } = {}, data) {
    return this.httpClient.post(`/contracts/${contractId}/claim-verifications`, {
      expectedStatus: 201,
      data: data ? {
        data
      } : void 0,
      params: {
        silent,
        sideEffects
      },
      schema: createClaimVerificationResBodySchema
    });
  }
  async rejectClaim(contractId, data, { silent = false, sideEffects = true } = {}) {
    return this.httpClient.post(`/contracts/${contractId}/claim-rejections`, {
      expectedStatus: 201,
      data: data ? {
        data
      } : {
        data: {}
      },
      params: {
        silent,
        sideEffects
      },
      schema: createClaimRejectionResBodySchema
    });
  }
  async createGoodwillGestureClaim(contractId, data, { silent = false, sideEffects = true } = {}) {
    return this.httpClient.post(`/contracts/${contractId}/claim-goodwill-gestures`, {
      expectedStatus: 201,
      data: data ? {
        data
      } : {
        data: {}
      },
      params: {
        silent,
        sideEffects
      },
      schema: createClaimGoodwillGestureResBodySchema
    });
  }
  async refundContract(contractId, { silent = false, sideEffects = true } = {}) {
    return this.httpClient.post(`/contracts/${contractId}/claim-refunds`, {
      expectedStatus: 201,
      params: {
        silent,
        sideEffects
      },
      schema: createClaimRefundResBodySchema
    });
  }
  async renounceContract({ contractId, data }) {
    return this.httpClient.delete(`/contracts/${contractId}`, {
      data: {
        data
      },
      expectedStatus: 204
    });
  }
  async deleteQuote(quoteId) {
    return this.httpClient.delete(`/quotes/${quoteId}`, {
      expectedStatus: 200,
      schema: deleteQuoteResBodySchema
    });
  }
  async findOneContract(contractId) {
    return this.httpClient.get(`/contracts/${contractId}`, {
      expectedStatus: 200,
      schema: findOneContractResBodySchema
    });
  }
  async createQuote({ data }) {
    return this.httpClient.post("/quotes", {
      data: {
        data
      },
      expectedStatus: 201,
      schema: createQuoteResBodySchema
    });
  }
  async createSubscription(data) {
    return this.httpClient.post("/subscriptions", {
      data: {
        data
      },
      expectedStatus: 201,
      schema: createSubscriptionResBodySchema
    });
  }
  async updateSubscription(bookingNumber, data) {
    return this.httpClient.patch(`/subscriptions/${bookingNumber}`, {
      data: {
        data
      },
      expectedStatus: [
        200,
        204
      ],
      schema: updateSubscriptionResBodySchema
    });
  }
  async createSubscriptionPayments(subscriptionId, payments) {
    return this.httpClient.post(`/subscriptions/${subscriptionId}/payments`, {
      data: {
        payments
      },
      expectedStatus: 201,
      schema: createSubscriptionPaymentsResBodySchema
    });
  }
  async createExchangeRateRequest({ date } = {}) {
    await this.httpClient.post("/exchange-rate-requests", {
      expectedStatus: 204,
      params: {
        date
      }
    });
  }
  async newFlightMonitoring({ ignoreFlightUpdatedAfter } = {
    ignoreFlightUpdatedAfter: void 0
  }) {
    const url = ignoreFlightUpdatedAfter === void 0 ? "/flights/monitorings" : `/flights/monitorings?ignoreFlightUpdatedAfter=${encodeURIComponent(ignoreFlightUpdatedAfter)}`;
    return this.httpClient.post(url, {
      expectedStatus: 201,
      schema: createFlightMonitoringResBodySchema
    });
  }
  async listPartners() {
    return this.httpClient.get("/partners", {
      expectedStatus: 200,
      schema: listPartnersResBodySchema
    });
  }
  async listSubscriptions(query = {}) {
    return this.httpClient.get("/subscriptions", {
      expectedStatus: 200,
      params: query,
      schema: listSubscriptionsResBodySchema
    });
  }
  async getSubscriptionContracts({ bookingNumber }) {
    return this.httpClient.get(`/subscriptions/${bookingNumber}/contracts`, {
      expectedStatus: 200,
      schema: getAllSubscriptionContractsResBodySchema
    });
  }
  async confirmBookingCancellation({ bookingNumber }) {
    return this.httpClient.post(`/bookings/${bookingNumber}/confirm-cancellation`, {
      expectedStatus: 200,
      schema: confirmBookingCancellationResBodySchema
    });
  }
  async updateSubscriptionBankDetails(id, data, { silent = false, sideEffects = true } = {}) {
    return this.httpClient.patch(`/subscriptions/${id}/bank-details`, {
      data,
      params: {
        silent,
        sideEffects
      },
      expectedStatus: [
        201,
        204
      ]
    });
  }
  async deleteClaim(claimId) {
    await this.httpClient.delete(`/claims/${claimId}`, {
      expectedStatus: 204
    });
  }
  async createBundleQuote({ partnerId, bundleId, data }) {
    return this.httpClient.post(`/partners/${partnerId}/bundles/${bundleId}/quote/reference`, {
      data: {
        data
      },
      schema: createBundleQuoteResBodySchema,
      expectedStatus: 201
    });
  }
  async upsertExternalClaim({ id: contractId, providerClaimId, data }) {
    return this.httpClient.put(`/contracts/${contractId}/external-claims/${encodeURIComponent(providerClaimId)}`, {
      data: {
        data
      },
      schema: upsertExternalClaimResBodySchema,
      expectedStatus: 200
    });
  }
  async getDocument(documentId) {
    return this.httpClient.get(`/documents/${documentId}`, {
      schema: getDocumentResBodySchema,
      expectedStatus: 200
    });
  }
  async updateDocument(documentId, data) {
    return this.httpClient.patch(`/documents/${documentId}`, {
      data: {
        data
      },
      schema: updateDocumentResBodySchema,
      expectedStatus: 200
    });
  }
  async replaceDocument(documentId, data) {
    return this.httpClient.post(`/documents/${documentId}/replacement`, {
      data: {
        data
      },
      schema: replaceDocumentResBodySchema,
      expectedStatus: 200
    });
  }
  async deleteStaleContractDocuments() {
    return this.httpClient.delete("/documents", {
      schema: deleteStaleContractDocumentsResBodySchema,
      expectedStatus: 200
    });
  }
  async expireSubscriptionPayments() {
    await this.httpClient.post("/subscriptions/payment-expirations", {
      expectedStatus: 204
    });
  }
  async createSubscriptionPaymentReminders() {
    await this.httpClient.post("/subscriptions/payment-reminders", {
      expectedStatus: 204
    });
  }
  async linkWiseRecipientContacts() {
    await this.httpClient.post("/wise/recipient-contact-links", {
      expectedStatus: 204
    });
  }
  async getDashboardReference() {
    return this.httpClient.get("/dashboard/reference", {
      schema: getDashboardReferenceResBodySchema
    });
  }
  async getDashboardSnapshot() {
    return this.httpClient.get("/dashboard/snapshot", {
      schema: getDashboardSnapshotResBodySchema
    });
  }
  async createWiseRecipients() {
    await this.httpClient.post("/wise/recipient-creations", {
      expectedStatus: 204
    });
  }
  async getPartnerOverview(partnerId) {
    return this.httpClient.get(`/partners/${partnerId}/overview`, {
      schema: getPartnerOverviewResBodySchema
    });
  }
  async resendSubscriptionConfirmationEmail(subscriptionId) {
    await this.httpClient.post(`/subscriptions/${subscriptionId}/confirmation-email`, {
      expectedStatus: 204
    });
  }
  async reopenPaymentWindow(subscriptionId, data) {
    return this.httpClient.post(`/subscriptions/${subscriptionId}/reopen-payment-window`, {
      data,
      expectedStatus: 201,
      schema: reopenPaymentWindowResBodySchema
    });
  }
};

// src/partner-api-customer-client.ts
import { HttpClient as HttpClient2, ResponseError, ResponseStatusError } from "@repo/lib-http-client";
import { ContractDocumentUploadStatuses } from "@repo/lib-koala-types";
import { sleep } from "@repo/lib-utils";
import { createCustomerContractClaimResBodySchema, createCustomerContractDocumentResBodySchema, createCustomerSubscriptionBankDetailsResBodySchema, createWiseRecipientResBodySchema, getCustomerContractDocumentResBodySchema, getCustomerContractLatestClaimResBodySchema, getCustomerContractWiseAccountRequirementsResBodySchema, getCustomerSubscriptionResBodySchema, getSubscriptionPaymentsResBodySchema, refreshWiseQuoteAccountRequirementsResBodySchema } from "@repo/schema-api-partner";

// src/polling-contract-document.error.ts
var PollingContractDocumentError = class extends Error {
  static {
    __name(this, "PollingContractDocumentError");
  }
  params;
  constructor(params) {
    super(`Polling for contract document "${params.documentId}" (contract: "${params.contractId}") timed out after ${params.timeoutMs}ms.`), this.params = params;
  }
};

// src/partner-api-customer-client.ts
var PartnerApiCustomerClient = class {
  static {
    __name(this, "PartnerApiCustomerClient");
  }
  httpClient;
  constructor({ baseUrl, customerToken, timeout, headers }) {
    this.httpClient = new HttpClient2({
      baseURL: baseUrl,
      headers: {
        Authorization: `Bearer ${customerToken}`,
        "Content-Type": "application/json",
        ...headers ?? {}
      },
      timeout: timeout ?? 2e3
    });
  }
  setCustomerToken(customerToken) {
    this.httpClient.setAuthorization(customerToken);
  }
  async getCustomerContractLatestClaim(id) {
    return this.httpClient.get(`/customers/contracts/${id}/claims/latest`, {
      expectedStatus: 200,
      schema: getCustomerContractLatestClaimResBodySchema
    });
  }
  async createCustomerContractClaim(id, data) {
    return this.httpClient.post(`/customers/contracts/${id}/claims`, {
      data,
      expectedStatus: 201,
      schema: createCustomerContractClaimResBodySchema
    });
  }
  async getCustomerSubscription() {
    return this.httpClient.get("/customers/subscriptions", {
      expectedStatus: 200,
      schema: getCustomerSubscriptionResBodySchema
    });
  }
  async getCustomerContractDocument(contractId, documentId) {
    return this.httpClient.get(`/customers/contracts/${contractId}/documents/${documentId}`, {
      expectedStatus: 200,
      schema: getCustomerContractDocumentResBodySchema
    });
  }
  async createCustomerContractDocument(contractId, data) {
    return this.httpClient.post(`/customers/contracts/${contractId}/documents`, {
      data: {
        data
      },
      expectedStatus: 201,
      schema: createCustomerContractDocumentResBodySchema
    });
  }
  async deleteCustomerContractDocument(contractId, documentId) {
    await this.httpClient.delete(`/customers/contracts/${contractId}/documents/${documentId}`, {
      expectedStatus: 204
    });
  }
  /**
  * Polls the getContractDocument endpoint until the document's uploadStatus
  * reaches a terminal state (COMPLETED or FAILED), or until the timeout is exceeded.
  *
  * @param params - The contract ID, the document ID and the polling options.
  * @returns A promise that resolves with the document details once a terminal status is reached.
  * @throws {PollingContractDocumentError} If the timeout is exceeded before a terminal status is found.
  * @throws If an error other than 404 occurs.
  */
  async pollForContractDocumentFinalUploadStatus(params) {
    const { contractId, documentId, intervalMs = 1e3, timeoutMs = 1e4 } = params;
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
      try {
        const response = await this.getCustomerContractDocument(contractId, documentId);
        if (response.data.uploadStatus === ContractDocumentUploadStatuses.COMPLETED || response.data.uploadStatus === ContractDocumentUploadStatuses.FAILED) {
          return response;
        }
      } catch (error) {
        if (this.isNotFoundError(error) === false) {
          throw error;
        }
      }
      await sleep(intervalMs);
    }
    throw new PollingContractDocumentError({
      contractId,
      documentId,
      timeoutMs
    });
  }
  async createCustomerSubscriptionBankDetails(subscriptionId, data) {
    return this.httpClient.post(`/customers/subscriptions/${subscriptionId}/bank-details`, {
      data,
      expectedStatus: 201,
      schema: createCustomerSubscriptionBankDetailsResBodySchema
    });
  }
  async createWiseRecipient(data) {
    return this.httpClient.post("/wise/recipients", {
      data,
      expectedStatus: 201,
      schema: createWiseRecipientResBodySchema
    });
  }
  async getCustomerContractWiseAccountRequirements(id) {
    return this.httpClient.get(`/customers/contracts/${id}/wise-account-requirements`, {
      expectedStatus: 200,
      schema: getCustomerContractWiseAccountRequirementsResBodySchema
    });
  }
  async refreshWiseQuoteAccountRequirements(quoteId, data) {
    return this.httpClient.post(`/wise/quotes/${quoteId}/account-requirements`, {
      data,
      expectedStatus: 200,
      schema: refreshWiseQuoteAccountRequirementsResBodySchema
    });
  }
  async getSubscriptionPayments(subscriptionId) {
    return this.httpClient.get(`/subscriptions/${subscriptionId}/payments`, {
      expectedStatus: 200,
      schema: getSubscriptionPaymentsResBodySchema
    });
  }
  isNotFoundError(error) {
    return error instanceof ResponseStatusError && error.receivedStatus === 404 || error instanceof ResponseError && error.statusCode === 404;
  }
};

// src/index.ts
import { ApiClientError, NoResponseError, RequestError, ResponseError as ResponseError2, ResponseStatusError as ResponseStatusError2, ResponseTypeError } from "@repo/lib-http-client";
export {
  ApiClientError,
  NoResponseError,
  PartnerApiClient,
  PartnerApiCustomerClient,
  PollingContractDocumentError,
  RequestError,
  ResponseError2 as ResponseError,
  ResponseStatusError2 as ResponseStatusError,
  ResponseTypeError
};
//# sourceMappingURL=index.js.map